import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { archiveNegociacaoCaso, closeNegociacaoCaso, exportNegociacaoCasosCsv, fetchNegociacaoCasos, pauseNegociacaoCaso, resumeNegociacaoCaso, selectNegociacaoCasos, selectNegociacaoCasosError, selectNegociacaoCasosLoading, selectNegociacaoCasosPagination, selectNegociacaoExportingCsv, selectNegociacaoTabCounts, selectNegociacoesListUi, selectNegociacoesSituacoes, setListUiActiveTab, setListUiFilters, setListUiPage, setListUiSearchQuery, setListUiSortConfig, unarchiveNegociacaoCaso } from '@/features/negociacoes/store';
import { bulkEncerrarNegociacaoCasos, cancelNegociacaoStartCaso, encerrarNegociacaoCaso } from '@/features/negociacoes/store/actions';
import type { FetchNegociacaoCasosInput } from '@/features/negociacoes/store/actions';
import type { PeriodoValor } from '@/shared/types/filtroPeriodo';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toast } from 'sonner';
import type { CadenciaBucket, CloseNegociacaoCasoResult, NegociacaoCasoStatus, TipoEncerramento } from '../types';
import type { NegociacaoListRow, NegociacoesActiveChip, NegociacoesFilterCategory, NegociacoesFilters, NegociacoesSortConfig, NegociacoesSortKey, NegociacoesTabValue } from '../types/negociacoesUiTypes';
import type { NegociacaoPeriodoCampo } from '../types';
import { buildPaginationRange, legendaDoPeriodo, PERIODO_NEGOCIACAO_VAZIO, rotuloDoChipPeriodo, toNegociacaoListRow } from '../utils';
import { useNegociacoesPeriodo } from './useNegociacoesPeriodo';
const tabLabelMap: Record<Exclude<NegociacoesTabValue, 'ALL'>, string> = {
  PENDING: 'Com pendência',
  ARCHIVED: 'Arquivadas'
};
const defaultFilters: NegociacoesFilters = {
  status: [],
  temperature: [],
  pendency: [],
  situacao: []
};
interface UseNegociacoesPageReturn {
  activeTab: NegociacoesTabValue;
  setActiveTab: (value: NegociacoesTabValue) => void;
  page: number;
  setPage: (value: number) => void;
  limit: number;
  isLoading: boolean;
  error: string | null;
  totalResults: number;
  totalPages: number;
  paginationText: string;
  searchInput: string;
  setSearchInput: (value: string) => void;
  searchQuery: string;
  filters: NegociacoesFilters;
  setFilters: Dispatch<SetStateAction<NegociacoesFilters>>;
  tempFilters: NegociacoesFilters;
  setTempFilters: Dispatch<SetStateAction<NegociacoesFilters>>;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (value: boolean) => void;
  hasActiveFilters: boolean;
  hasTempFilters: boolean;
  periodo: PeriodoValor;
  campoPeriodoAtivo: NegociacaoPeriodoCampo | null;
  legendaPeriodo: string;
  aplicarPeriodo: (valor: PeriodoValor) => void;
  atualizarRascunhoPeriodo: (valor: PeriodoValor | null) => void;
  resumoPreviaPeriodo: string | undefined;
  tempPeriodo: PeriodoValor;
  setTempPeriodo: Dispatch<SetStateAction<PeriodoValor>>;
  activeChips: NegociacoesActiveChip[];
  sortConfig: NegociacoesSortConfig;
  handleSort: (key: NegociacoesSortKey) => void;
  selectedIds: Set<string>;
  toggleSelectAll: () => void;
  toggleSelect: (id: string) => void;
  clearSelection: () => void;
  pauseCaso: (caseId: string) => Promise<void>;
  resumeCaso: (caseId: string) => Promise<void>;
  closeCaso: (caseId: string, resultStatus: CloseNegociacaoCasoResult['resultStatus']) => Promise<boolean>;
  encerrarCaso: (caseId: string, tipo: TipoEncerramento, observacoes: string, detalhesAcordo?: {
    valorAcordado: number;
    parcelas?: number;
    primeiroVencimento?: string;
  }) => Promise<boolean>;
  archiveCaso: (caseId: string) => Promise<void>;
  unarchiveCaso: (caseId: string) => Promise<void>;
  cancelStartCaso: (caseId: string) => Promise<void>;
  pauseSelected: () => Promise<void>;
  resumeSelected: () => Promise<void>;
  archiveSelected: () => Promise<void>;
  unarchiveSelected: () => Promise<void>;
  closeSelected: (tipo: TipoEncerramento, observacoes: string, detalhesAcordo?: {
    valorAcordado: number;
    parcelas?: number;
    primeiroVencimento?: string;
  }) => Promise<void>;
  rows: NegociacaoListRow[];
  openDrawer: () => void;
  applyFilters: () => void;
  clearTempFilters: () => void;
  clearAllFilters: () => void;
  removeChip: (chip: NegociacoesActiveChip) => void;
  toggleFilterValue: (category: NegociacoesFilterCategory, value: string) => void;
  clearFilterCategory: (category: NegociacoesFilterCategory) => void;
  toggleTempFilterValue: (category: NegociacoesFilterCategory, value: string) => void;
  selectAllTempInCategory: (category: NegociacoesFilterCategory, options: string[]) => void;
  retryFetch: () => void;
  exportCsv: (format: 'csv' | 'xlsx') => void;
  isExportingCsv: boolean;
  kpis: {
    resultsLabel: string;
    resultsValue: string;
    needsActionLabel: string;
    needsActionValue: string;
    overdueLabel: string;
    overdueValue: string;
    concludedLabel: string;
    concludedValue: string;
  };
  hasExistingNegociacoes: boolean;
  tabCounts: {
    all: number;
    open: number;
    concluded: number;
    closed: number;
    archived: number;
    withPendency: number;
  };
}
export const useNegociacoesPage = () => {
  const $ = _c(264);
  const dispatch = useAppDispatch();
  const casos = useAppSelector(selectNegociacaoCasos);
  const isLoading = useAppSelector(selectNegociacaoCasosLoading);
  const error = useAppSelector(selectNegociacaoCasosError);
  const pagination = useAppSelector(selectNegociacaoCasosPagination);
  const isExportingCsv = useAppSelector(selectNegociacaoExportingCsv);
  const tabCounts = useAppSelector(selectNegociacaoTabCounts);
  const listUi = useAppSelector(selectNegociacoesListUi);
  const activeTab = listUi.activeTab;
  let t0;
  if ($[0] !== dispatch) {
    t0 = value => dispatch(setListUiActiveTab(value));
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setActiveTab = t0;
  const page = listUi.page;
  const limit = 10;
  const [searchInput, setSearchInputLocal] = useState(listUi.searchQuery);
  const searchQuery = listUi.searchQuery;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const filters = listUi.filters;
  let t1;
  if ($[2] !== dispatch || $[3] !== listUi.filters) {
    t1 = updater => {
      if (typeof updater === "function") {
        dispatch(setListUiFilters(updater(listUi.filters)));
      } else {
        dispatch(setListUiFilters(updater));
      }
    };
    $[2] = dispatch;
    $[3] = listUi.filters;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setFilters = t1;
  const [tempFilters, setTempFilters] = useState(listUi.filters);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [selectedIds, setSelectedIds] = useState(t2);
  const sortConfig = listUi.sortConfig;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = value_0 => {
      setSearchInputLocal(value_0);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setSearchInput = t3;
  const autoRefreshedRef = useRef(false);
  const [hasExistingNegociacoes, setHasExistingNegociacoes] = useState(_temp);
  let t4;
  let t5;
  if ($[7] !== hasExistingNegociacoes || $[8] !== pagination.total) {
    t4 = () => {
      if (pagination.total > 0 && !hasExistingNegociacoes) {
        setHasExistingNegociacoes(true);
        window.localStorage.setItem("hasExistingNegociacoes", "true");
      }
    };
    t5 = [pagination.total, hasExistingNegociacoes];
    $[7] = hasExistingNegociacoes;
    $[8] = pagination.total;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    if (pagination.totalPages > 0) {
      t6 = pagination.totalPages;
      break bb0;
    }
    if (pagination.total <= 0) {
      t6 = 1;
      break bb0;
    }
    const safeLimit = pagination.limit > 0 ? pagination.limit : 10;
    const computedTotalPages = Math.ceil(pagination.total / safeLimit);
    t6 = computedTotalPages > 0 ? computedTotalPages : 1;
  }
  const maxPage = t6;
  let t7;
  if ($[11] !== dispatch || $[12] !== maxPage) {
    t7 = value_1 => {
      const next = value_1 < 1 ? 1 : value_1 > maxPage ? maxPage : value_1;
      dispatch(setListUiPage(next));
    };
    $[11] = dispatch;
    $[12] = maxPage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const setPage = t7;
  let t8;
  if ($[14] !== activeTab || $[15] !== dispatch || $[16] !== filters) {
    t8 = () => {
      if (activeTab === "ALL") {
        return;
      }
      if (filters.status.length > 0) {
        dispatch(setListUiFilters({
          ...filters,
          status: []
        }));
      }
      setTempFilters(_temp2);
    };
    $[14] = activeTab;
    $[15] = dispatch;
    $[16] = filters;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== activeTab || $[19] !== dispatch) {
    t9 = [activeTab, dispatch];
    $[18] = activeTab;
    $[19] = dispatch;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== dispatch || $[22] !== searchInput) {
    t10 = () => {
      const handle = setTimeout(() => {
        dispatch(setListUiSearchQuery(searchInput.trim()));
      }, 350);
      return () => clearTimeout(handle);
    };
    t11 = [searchInput, dispatch];
    $[21] = dispatch;
    $[22] = searchInput;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  bb1: {
    if (filters.temperature.length === 0) {
      t12 = undefined;
      break bb1;
    }
    let buckets;
    if ($[25] !== filters.temperature) {
      buckets = new Set();
      for (const temp of filters.temperature) {
        if (temp === "Quente") {
          buckets.add("HOT");
        }
        if (temp === "Fria") {
          buckets.add("COLD");
        }
        if (temp === "Morna") {
          buckets.add("WARM");
          buckets.add("ACTIVE");
        }
      }
      $[25] = filters.temperature;
      $[26] = buckets;
    } else {
      buckets = $[26];
    }
    let t13;
    if ($[27] !== buckets) {
      t13 = Array.from(buckets);
      $[27] = buckets;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    const result = t13;
    t12 = result.length > 0 ? result : undefined;
  }
  const cadenceBucketInFilter = t12;
  let t13;
  bb2: {
    if (filters.situacao.length === 0) {
      t13 = undefined;
      break bb2;
    }
    t13 = filters.situacao;
  }
  const situacaoIdInFilter = t13;
  let t14;
  bb3: {
    if (filters.pendency.length !== 1) {
      t14 = undefined;
      break bb3;
    }
    t14 = filters.pendency[0] === "Com pend\xEAncia" ? "WITH" : "WITHOUT";
  }
  const pendencyFilter = t14;
  let t15;
  bb4: {
    if (activeTab !== "ALL") {
      let t16;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {
          status: undefined,
          statusIn: undefined,
          humanIntervention: undefined
        };
        $[29] = t16;
      } else {
        t16 = $[29];
      }
      t15 = t16 as const;
      break bb4;
    }
    if (filters.status.length === 0) {
      let t16;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {
          status: undefined,
          statusIn: undefined,
          humanIntervention: undefined
        };
        $[30] = t16;
      } else {
        t16 = $[30];
      }
      t15 = t16 as const;
      break bb4;
    }
    let t16;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = ["Aguardando interven\xE7\xE3o humana"];
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    const humanInterventionLabels = t16;
    const hasHumanIntervention = filters.status.some(s => humanInterventionLabels.includes(s));
    let t17;
    if ($[32] !== filters.status) {
      let t18;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = s_0 => !humanInterventionLabels.includes(s_0);
        $[34] = t18;
      } else {
        t18 = $[34];
      }
      const selectedStatusLabels = filters.status.filter(t18);
      const labelToStatus = {
        "Aguardando envio": "SCHEDULED",
        "Aguardando resposta": "NEGOTIATION_STARTED",
        "Em negocia\xE7\xE3o": "ACTIVE",
        "Acordo fechado": "AGREEMENT_CLOSED",
        "Proposta recusada": "NO_AGREEMENT",
        Interrompida: "CLOSED",
        Pausada: "PAUSED",
        "Falha no envio": "FAILED"
      };
      t17 = selectedStatusLabels.map(label => labelToStatus[label]).filter(_temp3);
      $[32] = filters.status;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const selected = t17;
    if (selected.length === 0) {
      const t18 = hasHumanIntervention ? true : undefined;
      let t19;
      if ($[35] !== t18) {
        t19 = {
          status: undefined,
          statusIn: undefined,
          humanIntervention: t18
        };
        $[35] = t18;
        $[36] = t19;
      } else {
        t19 = $[36];
      }
      t15 = t19 as const;
      break bb4;
    }
    if (selected.length === 1) {
      const t18 = hasHumanIntervention ? true : undefined;
      let t19;
      if ($[37] !== selected[0] || $[38] !== t18) {
        t19 = {
          status: selected[0],
          statusIn: undefined,
          humanIntervention: t18
        };
        $[37] = selected[0];
        $[38] = t18;
        $[39] = t19;
      } else {
        t19 = $[39];
      }
      t15 = t19 as const;
      break bb4;
    }
    const t18 = hasHumanIntervention ? true : undefined;
    let t19;
    if ($[40] !== selected || $[41] !== t18) {
      t19 = {
        status: undefined,
        statusIn: selected,
        humanIntervention: t18
      };
      $[40] = selected;
      $[41] = t18;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    t15 = t19 as const;
  }
  const statusFiltersOnAllTab = t15;
  let t16;
  if ($[43] !== statusFiltersOnAllTab.humanIntervention || $[44] !== statusFiltersOnAllTab.status || $[45] !== statusFiltersOnAllTab.statusIn) {
    t16 = {
      status: statusFiltersOnAllTab.status,
      statusIn: statusFiltersOnAllTab.statusIn,
      humanIntervention: statusFiltersOnAllTab.humanIntervention
    };
    $[43] = statusFiltersOnAllTab.humanIntervention;
    $[44] = statusFiltersOnAllTab.status;
    $[45] = statusFiltersOnAllTab.statusIn;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const tabCountsFilters = t16;
  let t17;
  bb5: {
    const t18 = searchQuery || undefined;
    let t19;
    if ($[47] !== cadenceBucketInFilter || $[48] !== situacaoIdInFilter || $[49] !== t18) {
      t19 = {
        search: t18,
        cadenceBucketIn: cadenceBucketInFilter,
        situacaoIdIn: situacaoIdInFilter
      };
      $[47] = cadenceBucketInFilter;
      $[48] = situacaoIdInFilter;
      $[49] = t18;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    const comuns = t19;
    if (activeTab === "PENDING") {
      let t20;
      if ($[51] !== comuns) {
        t20 = {
          ...comuns,
          pendency: "WITH"
        };
        $[51] = comuns;
        $[52] = t20;
      } else {
        t20 = $[52];
      }
      t17 = t20;
      break bb5;
    }
    if (activeTab === "ARCHIVED") {
      let t20;
      if ($[53] !== comuns || $[54] !== pendencyFilter) {
        t20 = {
          ...comuns,
          archived: true,
          pendency: pendencyFilter
        };
        $[53] = comuns;
        $[54] = pendencyFilter;
        $[55] = t20;
      } else {
        t20 = $[55];
      }
      t17 = t20;
      break bb5;
    }
    let t20;
    if ($[56] !== comuns || $[57] !== pendencyFilter || $[58] !== statusFiltersOnAllTab.humanIntervention || $[59] !== statusFiltersOnAllTab.status || $[60] !== statusFiltersOnAllTab.statusIn) {
      t20 = {
        ...comuns,
        humanIntervention: statusFiltersOnAllTab.humanIntervention,
        status: statusFiltersOnAllTab.status,
        statusIn: statusFiltersOnAllTab.statusIn,
        pendency: pendencyFilter
      };
      $[56] = comuns;
      $[57] = pendencyFilter;
      $[58] = statusFiltersOnAllTab.humanIntervention;
      $[59] = statusFiltersOnAllTab.status;
      $[60] = statusFiltersOnAllTab.statusIn;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    t17 = t20;
  }
  const filtrosDaAba = t17;
  let t18;
  if ($[62] !== filtrosDaAba) {
    t18 = {
      filtrosDaLista: filtrosDaAba
    };
    $[62] = filtrosDaAba;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  const {
    periodo,
    parametrosApi: periodoApi,
    campoAtivo: campoPeriodoAtivo,
    aplicarPeriodo,
    removerPeriodo,
    atualizarRascunho: atualizarRascunhoPeriodo,
    resumoPrevia: resumoPreviaPeriodo
  } = useNegociacoesPeriodo(t18);
  const [tempPeriodo, setTempPeriodo] = useState(PERIODO_NEGOCIACAO_VAZIO);
  let t19;
  if ($[64] !== filtrosDaAba || $[65] !== periodoApi) {
    t19 = {
      ...filtrosDaAba,
      ...periodoApi
    };
    $[64] = filtrosDaAba;
    $[65] = periodoApi;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  const filtrosComPeriodo = t19;
  const t20 = sortConfig.key === "createdAt" && campoPeriodoAtivo === "acordo" ? "dataAcordo" : sortConfig.key;
  let t21;
  if ($[67] !== filtrosComPeriodo || $[68] !== page || $[69] !== sortConfig.direction || $[70] !== t20 || $[71] !== tabCountsFilters.humanIntervention || $[72] !== tabCountsFilters.status || $[73] !== tabCountsFilters.statusIn) {
    t21 = {
      ...filtrosComPeriodo,
      tabCountsStatus: tabCountsFilters.status,
      tabCountsStatusIn: tabCountsFilters.statusIn,
      tabCountsHumanIntervention: tabCountsFilters.humanIntervention,
      page,
      limit: 10,
      sortBy: t20,
      sortOrder: sortConfig.direction
    };
    $[67] = filtrosComPeriodo;
    $[68] = page;
    $[69] = sortConfig.direction;
    $[70] = t20;
    $[71] = tabCountsFilters.humanIntervention;
    $[72] = tabCountsFilters.status;
    $[73] = tabCountsFilters.statusIn;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  const parametrosDaLista = t21;
  let t22;
  if ($[75] !== dispatch) {
    t22 = () => {
      dispatch(setListUiPage(1));
    };
    $[75] = dispatch;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== cadenceBucketInFilter || $[78] !== dispatch || $[79] !== filters.pendency || $[80] !== periodoApi || $[81] !== situacaoIdInFilter || $[82] !== statusFiltersOnAllTab) {
    t23 = [cadenceBucketInFilter, periodoApi, situacaoIdInFilter, statusFiltersOnAllTab, filters.pendency, dispatch];
    $[77] = cadenceBucketInFilter;
    $[78] = dispatch;
    $[79] = filters.pendency;
    $[80] = periodoApi;
    $[81] = situacaoIdInFilter;
    $[82] = statusFiltersOnAllTab;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  useEffect(t22, t23);
  let t24;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setSelectedIds(new Set());
    };
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  let t25;
  if ($[85] !== periodoApi) {
    t25 = [periodoApi];
    $[85] = periodoApi;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[87] !== dispatch || $[88] !== parametrosDaLista) {
    t26 = () => {
      dispatch(fetchNegociacaoCasos(parametrosDaLista));
    };
    t27 = [dispatch, parametrosDaLista];
    $[87] = dispatch;
    $[88] = parametrosDaLista;
    $[89] = t26;
    $[90] = t27;
  } else {
    t26 = $[89];
    t27 = $[90];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[91] !== dispatch || $[92] !== maxPage || $[93] !== page) {
    t28 = () => {
      if (page > maxPage) {
        dispatch(setListUiPage(maxPage));
      }
    };
    t29 = [maxPage, page, dispatch];
    $[91] = dispatch;
    $[92] = maxPage;
    $[93] = page;
    $[94] = t28;
    $[95] = t29;
  } else {
    t28 = $[94];
    t29 = $[95];
  }
  useEffect(t28, t29);
  const situacoesOptions = useAppSelector(selectNegociacoesSituacoes);
  let map;
  if ($[96] !== situacoesOptions) {
    map = {};
    situacoesOptions.forEach(s_1 => {
      map[s_1.id] = s_1.label;
    });
    $[96] = situacoesOptions;
    $[97] = map;
  } else {
    map = $[97];
  }
  const situacaoLabelById = map;
  let chips;
  if ($[98] !== campoPeriodoAtivo || $[99] !== filters.pendency || $[100] !== filters.situacao || $[101] !== filters.status || $[102] !== filters.temperature || $[103] !== periodo || $[104] !== periodoApi.periodoInicio || $[105] !== situacaoLabelById) {
    chips = [];
    if (campoPeriodoAtivo) {
      chips.push({
        label: rotuloDoChipPeriodo(periodo),
        category: "periodo",
        value: periodoApi.periodoInicio ?? ""
      });
    }
    if (filters.status.length > 0) {
      filters.status.forEach(val => chips.push({
        label: `Status: ${val}`,
        category: "status",
        value: val
      }));
    }
    if (filters.temperature.length > 0) {
      filters.temperature.forEach(val_0 => chips.push({
        label: `Temp: ${val_0}`,
        category: "temperature",
        value: val_0
      }));
    }
    if (filters.pendency.length > 0) {
      filters.pendency.forEach(val_1 => chips.push({
        label: val_1,
        category: "pendency",
        value: val_1
      }));
    }
    if (filters.situacao.length > 0) {
      filters.situacao.forEach(id => chips.push({
        label: `Situação: ${situacaoLabelById[id] ?? id}`,
        category: "situacao",
        value: id
      }));
    }
    $[98] = campoPeriodoAtivo;
    $[99] = filters.pendency;
    $[100] = filters.situacao;
    $[101] = filters.status;
    $[102] = filters.temperature;
    $[103] = periodo;
    $[104] = periodoApi.periodoInicio;
    $[105] = situacaoLabelById;
    $[106] = chips;
  } else {
    chips = $[106];
  }
  const activeChips = chips;
  let t30;
  if ($[107] !== casos) {
    t30 = casos.map(toNegociacaoListRow);
    $[107] = casos;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  const rows = t30;
  const filteredAndSortedRows = rows;
  let t31;
  if ($[109] !== campoPeriodoAtivo || $[110] !== filters) {
    t31 = campoPeriodoAtivo !== null || Object.values(filters).some(_temp4);
    $[109] = campoPeriodoAtivo;
    $[110] = filters;
    $[111] = t31;
  } else {
    t31 = $[111];
  }
  const hasActiveFilters = t31;
  let t32;
  if ($[112] !== tempFilters || $[113] !== tempPeriodo.inicio) {
    t32 = Boolean(tempPeriodo.inicio) || Object.values(tempFilters).some(_temp5);
    $[112] = tempFilters;
    $[113] = tempPeriodo.inicio;
    $[114] = t32;
  } else {
    t32 = $[114];
  }
  const hasTempFilters = t32;
  let t33;
  if ($[115] !== campoPeriodoAtivo || $[116] !== periodo) {
    t33 = campoPeriodoAtivo ? legendaDoPeriodo(periodo) : "";
    $[115] = campoPeriodoAtivo;
    $[116] = periodo;
    $[117] = t33;
  } else {
    t33 = $[117];
  }
  const legendaPeriodo = t33;
  let t34;
  if ($[118] !== dispatch || $[119] !== sortConfig.direction || $[120] !== sortConfig.key) {
    t34 = key => {
      dispatch(setListUiSortConfig({
        key,
        direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc"
      }));
    };
    $[118] = dispatch;
    $[119] = sortConfig.direction;
    $[120] = sortConfig.key;
    $[121] = t34;
  } else {
    t34 = $[121];
  }
  const handleSort = t34;
  let t35;
  if ($[122] !== filteredAndSortedRows) {
    t35 = () => {
      setSelectedIds(prev_0 => {
        if (prev_0.size === filteredAndSortedRows.length) {
          return new Set();
        }
        return new Set(filteredAndSortedRows.map(_temp6));
      });
    };
    $[122] = filteredAndSortedRows;
    $[123] = t35;
  } else {
    t35 = $[123];
  }
  const toggleSelectAll = t35;
  let t36;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = id_0 => {
      setSelectedIds(prev_1 => {
        const next_0 = new Set(prev_1);
        if (next_0.has(id_0)) {
          next_0.delete(id_0);
          return next_0;
        }
        next_0.add(id_0);
        return next_0;
      });
    };
    $[124] = t36;
  } else {
    t36 = $[124];
  }
  const toggleSelect = t36;
  let t37;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => {
      setSelectedIds(new Set());
    };
    $[125] = t37;
  } else {
    t37 = $[125];
  }
  const clearSelection = t37;
  let t38;
  if ($[126] !== dispatch) {
    t38 = async caseId => {
      const result_0 = await dispatch(pauseNegociacaoCaso({
        caseId
      }));
      if (pauseNegociacaoCaso.fulfilled.match(result_0)) {
        toast.success("Negocia\xE7\xE3o pausada");
        return;
      }
      const errorMessage = typeof result_0.payload === "string" ? result_0.payload : result_0.error?.message || "Erro ao pausar negocia\xE7\xE3o";
      toast.error(errorMessage);
    };
    $[126] = dispatch;
    $[127] = t38;
  } else {
    t38 = $[127];
  }
  const pauseCaso = t38;
  let t39;
  if ($[128] !== dispatch) {
    t39 = async caseId_0 => {
      const result_1 = await dispatch(unarchiveNegociacaoCaso({
        caseId: caseId_0
      }));
      if (unarchiveNegociacaoCaso.fulfilled.match(result_1)) {
        toast.success("Negocia\xE7\xE3o desarquivada");
        return;
      }
      const errorMessage_0 = typeof result_1.payload === "string" ? result_1.payload : result_1.error?.message || "Erro ao desarquivar negocia\xE7\xE3o";
      toast.error(errorMessage_0);
    };
    $[128] = dispatch;
    $[129] = t39;
  } else {
    t39 = $[129];
  }
  const unarchiveCaso = t39;
  let t40;
  if ($[130] !== dispatch) {
    t40 = async caseId_1 => {
      const result_2 = await dispatch(archiveNegociacaoCaso({
        caseId: caseId_1
      }));
      if (archiveNegociacaoCaso.fulfilled.match(result_2)) {
        toast.success("Negocia\xE7\xE3o arquivada");
        return;
      }
      const errorMessage_1 = typeof result_2.payload === "string" ? result_2.payload : result_2.error?.message || "Erro ao arquivar negocia\xE7\xE3o";
      toast.error(errorMessage_1);
    };
    $[130] = dispatch;
    $[131] = t40;
  } else {
    t40 = $[131];
  }
  const archiveCaso = t40;
  let t41;
  if ($[132] !== dispatch) {
    t41 = async caseId_2 => {
      const result_3 = await dispatch(resumeNegociacaoCaso({
        caseId: caseId_2
      }));
      if (resumeNegociacaoCaso.fulfilled.match(result_3)) {
        toast.success("Negocia\xE7\xE3o reiniciada");
        return;
      }
      const errorMessage_2 = typeof result_3.payload === "string" ? result_3.payload : result_3.error?.message || "Erro ao reiniciar negocia\xE7\xE3o";
      toast.error(errorMessage_2);
    };
    $[132] = dispatch;
    $[133] = t41;
  } else {
    t41 = $[133];
  }
  const resumeCaso = t41;
  let t42;
  if ($[134] !== dispatch || $[135] !== filteredAndSortedRows || $[136] !== selectedIds) {
    t42 = async () => {
      const selected_0 = filteredAndSortedRows.filter(n_0 => selectedIds.has(n_0.id));
      const idsToPause = selected_0.filter(_temp7).map(_temp8);
      if (idsToPause.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser pausada");
        return;
      }
      const results = await Promise.all(idsToPause.map(id_1 => dispatch(pauseNegociacaoCaso({
        caseId: id_1
      }))));
      const successCount = results.filter(_temp9).length;
      const failCount = results.length - successCount;
      if (successCount > 0 && failCount === 0) {
        toast.success("Negocia\xE7\xF5es pausadas");
        clearSelection();
        return;
      }
      if (successCount > 0) {
        toast.success(`${successCount} negociações pausadas`);
        clearSelection();
      }
      if (failCount > 0) {
        toast.error(`${failCount} negociações não puderam ser pausadas`);
      }
    };
    $[134] = dispatch;
    $[135] = filteredAndSortedRows;
    $[136] = selectedIds;
    $[137] = t42;
  } else {
    t42 = $[137];
  }
  const pauseSelected = t42;
  let t43;
  if ($[138] !== dispatch || $[139] !== filteredAndSortedRows || $[140] !== selectedIds) {
    t43 = async () => {
      const selected_1 = filteredAndSortedRows.filter(n_3 => selectedIds.has(n_3.id));
      const idsToResume = selected_1.filter(_temp10).map(_temp11);
      if (idsToResume.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser reiniciada");
        return;
      }
      const results_0 = await Promise.all(idsToResume.map(id_2 => dispatch(resumeNegociacaoCaso({
        caseId: id_2
      }))));
      const successCount_0 = results_0.filter(_temp12).length;
      const failCount_0 = results_0.length - successCount_0;
      if (successCount_0 > 0 && failCount_0 === 0) {
        toast.success("Negocia\xE7\xF5es reiniciadas");
        clearSelection();
        return;
      }
      if (successCount_0 > 0) {
        toast.success(`${successCount_0} negociações reiniciadas`);
        clearSelection();
      }
      if (failCount_0 > 0) {
        toast.error(`${failCount_0} negociações não puderam ser reiniciadas`);
      }
    };
    $[138] = dispatch;
    $[139] = filteredAndSortedRows;
    $[140] = selectedIds;
    $[141] = t43;
  } else {
    t43 = $[141];
  }
  const resumeSelected = t43;
  let t44;
  if ($[142] !== dispatch || $[143] !== filteredAndSortedRows || $[144] !== selectedIds) {
    t44 = async () => {
      const idsToArchive = filteredAndSortedRows.filter(n_6 => selectedIds.has(n_6.id)).map(_temp13);
      if (idsToArchive.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser arquivada");
        return;
      }
      const results_1 = await Promise.all(idsToArchive.map(id_3 => dispatch(archiveNegociacaoCaso({
        caseId: id_3
      }))));
      const successCount_1 = results_1.filter(_temp14).length;
      const failCount_1 = results_1.length - successCount_1;
      if (successCount_1 > 0 && failCount_1 === 0) {
        toast.success("Negocia\xE7\xF5es arquivadas");
        clearSelection();
        return;
      }
      if (successCount_1 > 0) {
        toast.success(`${successCount_1} negociações arquivadas`);
        clearSelection();
      }
      if (failCount_1 > 0) {
        toast.error(`${failCount_1} negociações não puderam ser arquivadas`);
      }
    };
    $[142] = dispatch;
    $[143] = filteredAndSortedRows;
    $[144] = selectedIds;
    $[145] = t44;
  } else {
    t44 = $[145];
  }
  const archiveSelected = t44;
  let t45;
  if ($[146] !== dispatch || $[147] !== filteredAndSortedRows || $[148] !== selectedIds) {
    t45 = async () => {
      const idsToUnarchive = filteredAndSortedRows.filter(n_8 => selectedIds.has(n_8.id)).map(_temp15);
      if (idsToUnarchive.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser desarquivada");
        return;
      }
      const results_2 = await Promise.all(idsToUnarchive.map(id_4 => dispatch(unarchiveNegociacaoCaso({
        caseId: id_4
      }))));
      const successCount_2 = results_2.filter(_temp16).length;
      const failCount_2 = results_2.length - successCount_2;
      if (successCount_2 > 0 && failCount_2 === 0) {
        toast.success("Negocia\xE7\xF5es desarquivadas");
        clearSelection();
        return;
      }
      if (successCount_2 > 0) {
        toast.success(`${successCount_2} negociações desarquivadas`);
        clearSelection();
      }
      if (failCount_2 > 0) {
        toast.error(`${failCount_2} negociações não puderam ser desarquivadas`);
      }
    };
    $[146] = dispatch;
    $[147] = filteredAndSortedRows;
    $[148] = selectedIds;
    $[149] = t45;
  } else {
    t45 = $[149];
  }
  const unarchiveSelected = t45;
  let t46;
  if ($[150] !== filters || $[151] !== periodo) {
    t46 = () => {
      setTempFilters(filters);
      setTempPeriodo(periodo);
      setIsDrawerOpen(true);
    };
    $[150] = filters;
    $[151] = periodo;
    $[152] = t46;
  } else {
    t46 = $[152];
  }
  const openDrawer = t46;
  let t47;
  if ($[153] !== aplicarPeriodo || $[154] !== dispatch || $[155] !== tempFilters || $[156] !== tempPeriodo) {
    t47 = () => {
      dispatch(setListUiFilters(tempFilters));
      aplicarPeriodo(tempPeriodo);
      setIsDrawerOpen(false);
    };
    $[153] = aplicarPeriodo;
    $[154] = dispatch;
    $[155] = tempFilters;
    $[156] = tempPeriodo;
    $[157] = t47;
  } else {
    t47 = $[157];
  }
  const applyFilters = t47;
  let t48;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => {
      setTempFilters(defaultFilters);
      setTempPeriodo(PERIODO_NEGOCIACAO_VAZIO);
    };
    $[158] = t48;
  } else {
    t48 = $[158];
  }
  const clearTempFilters = t48;
  let t49;
  if ($[159] !== dispatch || $[160] !== removerPeriodo) {
    t49 = () => {
      dispatch(setListUiFilters(defaultFilters));
      setTempFilters(defaultFilters);
      removerPeriodo();
    };
    $[159] = dispatch;
    $[160] = removerPeriodo;
    $[161] = t49;
  } else {
    t49 = $[161];
  }
  const clearAllFilters = t49;
  let t50;
  if ($[162] !== dispatch || $[163] !== filters || $[164] !== removerPeriodo) {
    t50 = chip => {
      if (chip.category === "periodo") {
        removerPeriodo();
        return;
      }
      const next_1 = filters[chip.category].filter(v_2 => v_2 !== chip.value);
      dispatch(setListUiFilters({
        ...filters,
        [chip.category]: next_1
      }));
    };
    $[162] = dispatch;
    $[163] = filters;
    $[164] = removerPeriodo;
    $[165] = t50;
  } else {
    t50 = $[165];
  }
  const removeChip = t50;
  let t51;
  if ($[166] !== dispatch || $[167] !== filters) {
    t51 = (category, value_2) => {
      const current = filters[category];
      const next_2 = current.includes(value_2) ? current.filter(v_3 => v_3 !== value_2) : [...current, value_2];
      dispatch(setListUiFilters({
        ...filters,
        [category]: next_2
      }));
    };
    $[166] = dispatch;
    $[167] = filters;
    $[168] = t51;
  } else {
    t51 = $[168];
  }
  const toggleFilterValue = t51;
  let t52;
  if ($[169] !== dispatch || $[170] !== filters) {
    t52 = category_0 => {
      dispatch(setListUiFilters({
        ...filters,
        [category_0]: []
      }));
    };
    $[169] = dispatch;
    $[170] = filters;
    $[171] = t52;
  } else {
    t52 = $[171];
  }
  const clearFilterCategory = t52;
  let t53;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = (category_1, value_3) => {
      setTempFilters(prev_2 => {
        const current_0 = prev_2[category_1];
        const next_3 = current_0.includes(value_3) ? current_0.filter(v_4 => v_4 !== value_3) : [...current_0, value_3];
        return {
          ...prev_2,
          [category_1]: next_3
        };
      });
    };
    $[172] = t53;
  } else {
    t53 = $[172];
  }
  const toggleTempFilterValue = t53;
  let t54;
  if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = (category_2, options) => {
      setTempFilters(prev_3 => ({
        ...prev_3,
        [category_2]: options
      }));
    };
    $[173] = t54;
  } else {
    t54 = $[173];
  }
  const selectAllTempInCategory = t54;
  let t55;
  if ($[174] !== dispatch || $[175] !== parametrosDaLista) {
    t55 = () => {
      dispatch(fetchNegociacaoCasos(parametrosDaLista));
    };
    $[174] = dispatch;
    $[175] = parametrosDaLista;
    $[176] = t55;
  } else {
    t55 = $[176];
  }
  const retryFetch = t55;
  let t56;
  if ($[177] !== dispatch || $[178] !== filteredAndSortedRows || $[179] !== retryFetch || $[180] !== selectedIds) {
    t56 = async (tipo, observacoes, detalhesAcordo) => {
      const selected_2 = filteredAndSortedRows.filter(n_10 => selectedIds.has(n_10.id));
      const idsToClose = selected_2.filter(_temp17).map(_temp18);
      if (idsToClose.length === 0) {
        toast.error("Nenhuma negocia\xE7\xE3o selecionada pode ser encerrada");
        return;
      }
      const result_4 = await dispatch(bulkEncerrarNegociacaoCasos({
        caseIds: idsToClose,
        tipo,
        observacoes,
        detalhesAcordo
      }));
      if (bulkEncerrarNegociacaoCasos.fulfilled.match(result_4)) {
        const {
          successCount: successCount_3,
          failCount: failCount_3
        } = result_4.payload;
        if (successCount_3 > 0) {
          toast.success(`${successCount_3} negociações encerradas`);
          clearSelection();
          retryFetch();
        }
        if (failCount_3 > 0) {
          toast.error(`${failCount_3} negociações não puderam ser encerradas`);
        }
        return;
      }
      toast.error(typeof result_4.payload === "string" ? result_4.payload : "Erro ao encerrar negocia\xE7\xF5es");
    };
    $[177] = dispatch;
    $[178] = filteredAndSortedRows;
    $[179] = retryFetch;
    $[180] = selectedIds;
    $[181] = t56;
  } else {
    t56 = $[181];
  }
  const closeSelected = t56;
  let t57;
  if ($[182] !== dispatch || $[183] !== retryFetch) {
    t57 = async (caseId_3, resultStatus) => {
      const result_5 = await dispatch(closeNegociacaoCaso({
        caseId: caseId_3,
        resultStatus
      }));
      if (closeNegociacaoCaso.fulfilled.match(result_5)) {
        toast.success("Negocia\xE7\xE3o encerrada");
        retryFetch();
        return true;
      }
      const errorMessage_3 = typeof result_5.payload === "string" ? result_5.payload : result_5.error?.message || "Erro ao encerrar negocia\xE7\xE3o";
      toast.error(errorMessage_3);
      return false;
    };
    $[182] = dispatch;
    $[183] = retryFetch;
    $[184] = t57;
  } else {
    t57 = $[184];
  }
  const closeCaso = t57;
  let t58;
  if ($[185] !== dispatch || $[186] !== retryFetch) {
    t58 = async (caseId_4, tipo_0, observacoes_0, detalhesAcordo_0) => {
      const result_6 = await dispatch(encerrarNegociacaoCaso({
        caseId: caseId_4,
        tipo: tipo_0,
        observacoes: observacoes_0,
        detalhesAcordo: detalhesAcordo_0,
        encerrarAtendimentoHumano: true
      }));
      if (encerrarNegociacaoCaso.fulfilled.match(result_6)) {
        toast.success("Negocia\xE7\xE3o encerrada");
        retryFetch();
        return true;
      }
      const errorMessage_4 = typeof result_6.payload === "string" ? result_6.payload : result_6.error?.message || "Erro ao encerrar negocia\xE7\xE3o";
      toast.error(errorMessage_4);
      return false;
    };
    $[185] = dispatch;
    $[186] = retryFetch;
    $[187] = t58;
  } else {
    t58 = $[187];
  }
  const encerrarCaso = t58;
  let t59;
  if ($[188] !== dispatch || $[189] !== retryFetch) {
    t59 = async caseId_5 => {
      const result_7 = await dispatch(cancelNegociacaoStartCaso({
        caseId: caseId_5
      }));
      if (cancelNegociacaoStartCaso.fulfilled.match(result_7)) {
        toast.success("Agendamento cancelado");
        retryFetch();
        return;
      }
      const errorMessage_5 = typeof result_7.payload === "string" ? result_7.payload : result_7.error?.message || "Erro ao cancelar agendamento";
      toast.error(errorMessage_5);
    };
    $[188] = dispatch;
    $[189] = retryFetch;
    $[190] = t59;
  } else {
    t59 = $[190];
  }
  const cancelStartCaso = t59;
  let t60;
  let t61;
  if ($[191] !== retryFetch) {
    t60 = () => {
      if (autoRefreshedRef.current) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      const shouldRefresh = window.sessionStorage.getItem("negociacoesShouldRefresh") === "1";
      if (!shouldRefresh) {
        return;
      }
      autoRefreshedRef.current = true;
      window.sessionStorage.removeItem("negociacoesShouldRefresh");
      retryFetch();
      const handle_0 = window.setTimeout(() => {
        retryFetch();
      }, 1200);
      return () => {
        window.clearTimeout(handle_0);
      };
    };
    t61 = [retryFetch];
    $[191] = retryFetch;
    $[192] = t60;
    $[193] = t61;
  } else {
    t60 = $[192];
    t61 = $[193];
  }
  useEffect(t60, t61);
  let t62;
  if ($[194] !== dispatch || $[195] !== filtrosComPeriodo) {
    t62 = format => {
      (async () => {
        const result_8 = await dispatch(exportNegociacaoCasosCsv({
          ...filtrosComPeriodo,
          format
        }));
        if (exportNegociacaoCasosCsv.fulfilled.match(result_8)) {
          if (result_8.payload.truncated) {
            toast.warning("Exporta\xE7\xE3o parcial: limite m\xE1ximo de linhas atingido. Refine os filtros para incluir todos os registros.");
          } else {
            toast.success(`${format === "xlsx" ? "Excel" : "CSV"} exportado com sucesso`);
          }
          return;
        }
        const errorMessage_6 = typeof result_8.payload === "string" ? result_8.payload : result_8.error?.message || "Erro ao exportar CSV";
        toast.error(errorMessage_6);
      })();
    };
    $[194] = dispatch;
    $[195] = filtrosComPeriodo;
    $[196] = t62;
  } else {
    t62 = $[196];
  }
  const exportCsv = t62;
  let t63;
  if ($[197] !== page || $[198] !== pagination.total) {
    t63 = buildPaginationRange({
      page,
      limit: 10,
      total: pagination.total
    });
    $[197] = page;
    $[198] = pagination.total;
    $[199] = t63;
  } else {
    t63 = $[199];
  }
  const paginationRange = t63;
  let t64;
  bb6: {
    if (activeTab === "ALL") {
      t64 = "Resultados";
      break bb6;
    }
    t64 = `Resultados (${tabLabelMap[activeTab]})`;
  }
  const resultsLabel = t64;
  let t65;
  if ($[200] !== filteredAndSortedRows) {
    t65 = filteredAndSortedRows.filter(_temp19);
    $[200] = filteredAndSortedRows;
    $[201] = t65;
  } else {
    t65 = $[201];
  }
  const needsActionCount = t65.length;
  let t66;
  if ($[202] !== filteredAndSortedRows) {
    t66 = filteredAndSortedRows.filter(_temp20);
    $[202] = filteredAndSortedRows;
    $[203] = t66;
  } else {
    t66 = $[203];
  }
  const concludedCount = t66.length;
  const t67 = String(pagination.total);
  const t68 = String(needsActionCount);
  const t69 = String(concludedCount);
  let t70;
  if ($[204] !== resultsLabel || $[205] !== t67 || $[206] !== t68 || $[207] !== t69) {
    t70 = {
      resultsLabel,
      resultsValue: t67,
      needsActionLabel: "Aguardando a\xE7\xE3o",
      needsActionValue: t68,
      overdueLabel: "Em atraso",
      overdueValue: "0",
      concludedLabel: "Conclu\xEDdas",
      concludedValue: t69
    };
    $[204] = resultsLabel;
    $[205] = t67;
    $[206] = t68;
    $[207] = t69;
    $[208] = t70;
  } else {
    t70 = $[208];
  }
  const kpis = t70;
  const t71 = error ?? null;
  let t72;
  if ($[209] !== activeChips || $[210] !== activeTab || $[211] !== aplicarPeriodo || $[212] !== applyFilters || $[213] !== archiveCaso || $[214] !== archiveSelected || $[215] !== atualizarRascunhoPeriodo || $[216] !== campoPeriodoAtivo || $[217] !== cancelStartCaso || $[218] !== clearAllFilters || $[219] !== clearFilterCategory || $[220] !== closeCaso || $[221] !== closeSelected || $[222] !== encerrarCaso || $[223] !== exportCsv || $[224] !== filteredAndSortedRows || $[225] !== filters || $[226] !== handleSort || $[227] !== hasActiveFilters || $[228] !== hasExistingNegociacoes || $[229] !== hasTempFilters || $[230] !== isDrawerOpen || $[231] !== isExportingCsv || $[232] !== isLoading || $[233] !== kpis || $[234] !== legendaPeriodo || $[235] !== maxPage || $[236] !== openDrawer || $[237] !== page || $[238] !== pagination.total || $[239] !== paginationRange.text || $[240] !== pauseCaso || $[241] !== pauseSelected || $[242] !== periodo || $[243] !== removeChip || $[244] !== resumeCaso || $[245] !== resumeSelected || $[246] !== resumoPreviaPeriodo || $[247] !== retryFetch || $[248] !== searchInput || $[249] !== searchQuery || $[250] !== selectedIds || $[251] !== setActiveTab || $[252] !== setFilters || $[253] !== setPage || $[254] !== sortConfig || $[255] !== t71 || $[256] !== tabCounts || $[257] !== tempFilters || $[258] !== tempPeriodo || $[259] !== toggleFilterValue || $[260] !== toggleSelectAll || $[261] !== unarchiveCaso || $[262] !== unarchiveSelected) {
    t72 = {
      activeTab,
      setActiveTab,
      page,
      setPage,
      limit: 10,
      isLoading,
      error: t71,
      totalResults: pagination.total,
      totalPages: maxPage,
      paginationText: paginationRange.text,
      searchInput,
      setSearchInput,
      searchQuery,
      filters,
      setFilters,
      tempFilters,
      setTempFilters,
      isDrawerOpen,
      setIsDrawerOpen,
      hasActiveFilters,
      hasTempFilters,
      periodo,
      campoPeriodoAtivo,
      legendaPeriodo,
      aplicarPeriodo,
      atualizarRascunhoPeriodo,
      resumoPreviaPeriodo,
      tempPeriodo,
      setTempPeriodo,
      activeChips,
      sortConfig,
      handleSort,
      selectedIds,
      toggleSelectAll,
      toggleSelect,
      clearSelection,
      pauseCaso,
      resumeCaso,
      closeCaso,
      encerrarCaso,
      archiveCaso,
      unarchiveCaso,
      cancelStartCaso,
      pauseSelected,
      resumeSelected,
      archiveSelected,
      unarchiveSelected,
      closeSelected,
      rows: filteredAndSortedRows,
      openDrawer,
      applyFilters,
      clearTempFilters,
      clearAllFilters,
      removeChip,
      toggleFilterValue,
      clearFilterCategory,
      toggleTempFilterValue,
      selectAllTempInCategory,
      retryFetch,
      exportCsv,
      isExportingCsv,
      kpis,
      hasExistingNegociacoes,
      tabCounts
    };
    $[209] = activeChips;
    $[210] = activeTab;
    $[211] = aplicarPeriodo;
    $[212] = applyFilters;
    $[213] = archiveCaso;
    $[214] = archiveSelected;
    $[215] = atualizarRascunhoPeriodo;
    $[216] = campoPeriodoAtivo;
    $[217] = cancelStartCaso;
    $[218] = clearAllFilters;
    $[219] = clearFilterCategory;
    $[220] = closeCaso;
    $[221] = closeSelected;
    $[222] = encerrarCaso;
    $[223] = exportCsv;
    $[224] = filteredAndSortedRows;
    $[225] = filters;
    $[226] = handleSort;
    $[227] = hasActiveFilters;
    $[228] = hasExistingNegociacoes;
    $[229] = hasTempFilters;
    $[230] = isDrawerOpen;
    $[231] = isExportingCsv;
    $[232] = isLoading;
    $[233] = kpis;
    $[234] = legendaPeriodo;
    $[235] = maxPage;
    $[236] = openDrawer;
    $[237] = page;
    $[238] = pagination.total;
    $[239] = paginationRange.text;
    $[240] = pauseCaso;
    $[241] = pauseSelected;
    $[242] = periodo;
    $[243] = removeChip;
    $[244] = resumeCaso;
    $[245] = resumeSelected;
    $[246] = resumoPreviaPeriodo;
    $[247] = retryFetch;
    $[248] = searchInput;
    $[249] = searchQuery;
    $[250] = selectedIds;
    $[251] = setActiveTab;
    $[252] = setFilters;
    $[253] = setPage;
    $[254] = sortConfig;
    $[255] = t71;
    $[256] = tabCounts;
    $[257] = tempFilters;
    $[258] = tempPeriodo;
    $[259] = toggleFilterValue;
    $[260] = toggleSelectAll;
    $[261] = unarchiveCaso;
    $[262] = unarchiveSelected;
    $[263] = t72;
  } else {
    t72 = $[263];
  }
  return t72;
};
function _temp() {
  if (typeof window === "undefined") {
    return false;
  }
  return window.localStorage.getItem("hasExistingNegociacoes") === "true";
}
function _temp2(prev) {
  if (prev.status.length === 0) {
    return prev;
  }
  return {
    ...prev,
    status: []
  };
}
function _temp3(v) {
  return Boolean(v);
}
function _temp4(v_0) {
  return v_0.length > 0;
}
function _temp5(v_1) {
  return v_1.length > 0;
}
function _temp6(n) {
  return n.id;
}
function _temp7(n_1) {
  return n_1.statusValue !== "PAUSED" && n_1.statusValue !== "CLOSED" && n_1.statusValue !== "AGREEMENT_CLOSED" && n_1.statusValue !== "NO_AGREEMENT" && !n_1.isArchived;
}
function _temp8(n_2) {
  return n_2.id;
}
function _temp9(r) {
  return pauseNegociacaoCaso.fulfilled.match(r);
}
function _temp10(n_4) {
  return n_4.statusValue === "PAUSED";
}
function _temp11(n_5) {
  return n_5.id;
}
function _temp12(r_0) {
  return resumeNegociacaoCaso.fulfilled.match(r_0);
}
function _temp13(n_7) {
  return n_7.id;
}
function _temp14(r_1) {
  return archiveNegociacaoCaso.fulfilled.match(r_1);
}
function _temp15(n_9) {
  return n_9.id;
}
function _temp16(r_2) {
  return unarchiveNegociacaoCaso.fulfilled.match(r_2);
}
function _temp17(n_11) {
  return n_11.statusValue !== "CLOSED" && n_11.statusValue !== "AGREEMENT_CLOSED" && n_11.statusValue !== "NO_AGREEMENT" && !n_11.isArchived;
}
function _temp18(n_12) {
  return n_12.id;
}
function _temp19(n_13) {
  return n_13.needsAction;
}
function _temp20(n_14) {
  return n_14.statusValue === "AGREEMENT_CLOSED" || n_14.statusValue === "NO_AGREEMENT";
}